import {
  COPIED_ENTITY_SETS,
  D365RequestError,
  RECURRING_JOBS,
  RECURRING_TERMINAL,
  TransientD365Error,
  type D365Client,
} from '@lg/d365-client';
import {
  DMF_TERMINAL_STATUSES,
  GL_ACCOUNTANT_SLOT,
  approverEmail,
  describeAccountOverlay,
  type ApproverSeat,
  type EnvProfile,
  describeJournalOverlay,
  templateLedgerFor,
  type Address,
  type Answers,
  type PeopleData,
  type StepKey,
  type TemplateConfig,
} from '@lg/shared';
import { projectsInCreationOrder } from '../domain/people/masterData.js';
import { parsePeopleRows } from '../domain/people/parse.js';
import { buildRoutingPeople, type ExistingPosition, type PeopleContext } from '../domain/people/validate.js';
import { computeCoverage, coverageAmounts, type CoverageMatrix } from '../domain/routing.js';
import { buildPeoplePackage, deriveUserIds, peoplePackageEnv, zipPackage } from '../package/dataPackage.js';
import { ledgerOverlaysOf, workCalendarSpec } from '../package/templatePackage.js';
import type { RunRecord, TaskRecord } from '../store/records.js';

/**
 * Provisioning activities (spec 11.1). Each checks its skip condition first,
 * does its D365 work, and returns a result or throws StepFailure. The
 * browser runner and (later) the Durable Functions orchestrator both call
 * these. No activity deletes anything in D365.
 */

export class StepFailure extends Error {}

/**
 * Make sure every user on the users sheet exists in D365, creating the missing ones with the
 * same user IDs the people package uses (so nobody is created twice). Returns UPN (lower case)
 * → D365 user ID, and how many were created.
 */
async function ensureUsers(
  env: ActivityEnv,
  data: PeopleData,
): Promise<{ byUpn: Map<string, string>; created: number }> {
  const userIds = deriveUserIds(data.users);
  const byUpn = new Map<string, string>();
  let created = 0;
  for (const u of data.users) {
    const key = u.upn.toLowerCase();
    if (byUpn.has(key)) continue;
    const existing = (await inContext(`Looking up user ${u.upn}`, env.d365.searchUsers(u.upn))).find(
      (x) => x.upn.toLowerCase() === key,
    );
    if (existing) {
      byUpn.set(key, existing.userId);
      continue;
    }
    const newId = userIds.get(u.upn);
    if (!newId) throw new StepFailure(`No user ID could be derived for ${u.upn}`);
    const user = await inContext(
      `Creating user ${newId} (${u.upn})`,
      env.d365.createUser({ userId: newId, upn: u.upn, name: u.displayName, companyId: env.companyId }),
    );
    byUpn.set(key, user.userId);
    created += 1;
  }
  return { byUpn, created };
}

/** The worker the GL accountant position goes to: the first person on the roles sheet. */
export function accountantWorker(data: PeopleData): PeopleData['workers'][number] | undefined {
  const upn = data.roles[0]?.upn.toLowerCase();
  return upn ? data.workers.find((w) => w.upn.toLowerCase() === upn) : undefined;
}

interface AccountantSeat {
  personnelNumber: string;
  name: string;
  /** Employment start, and the position's and assignment's start */
  start: string;
}

/**
 * The profile's accountant (an existing D365 user, e.g. the prospect's test account): their worker
 * (found through the user's Person, or created and linked) employed in this company.
 */
async function ensureProfileAccountant(
  env: ActivityEnv,
  upn: string,
  today: string,
): Promise<AccountantSeat> {
  const user = (await inContext(`Looking up user ${upn}`, env.d365.searchUsers(upn))).find(
    (u) => u.upn.toLowerCase() === upn.toLowerCase(),
  );
  if (!user) throw new StepFailure(`${upn} is not a D365 user in this environment; add the user first`);
  const linked = await inContext(
    `Finding the worker linked to ${user.userId}`,
    env.d365.findWorkerForUser(user.userId),
  );
  const personnelNumber = linked?.personnelNumber ?? env.profile.accountantPersonnelNumber;
  const [first = user.userId, ...rest] = user.name.trim().split(/\s+/);
  await inContext(
    `Employing ${user.userId} (worker ${personnelNumber}) in ${env.companyId}`,
    env.d365.ensureWorker({
      companyId: env.companyId,
      personnelNumber,
      firstName: first.slice(0, 25),
      lastName: (rest.join(' ') || user.userId).slice(0, 25),
      email: user.upn,
      startDate: today,
    }),
  );
  if (!linked)
    await inContext(
      `Linking user ${user.userId} to worker ${personnelNumber}`,
      env.d365.ensurePersonUser({ userId: user.userId, personnelNumber, validFrom: today }),
    );
  return { personnelNumber, name: user.name, start: today };
}

/**
 * The profile's approval chain (e.g. General manager → Finance director → CFO), created once and
 * shared by every venue: jobs, positions (numbered by D365, found again by job), their fictional
 * workers and the Line reporting lines between them. A seat that already exists (an earlier venue
 * created it, or the environment had one) is used as it is: never given another worker. Returns
 * the bottom seat the venue's accountant reports to.
 */
async function ensureApproverChain(
  env: ActivityEnv,
  seats: readonly ApproverSeat[],
  today: string,
): Promise<{ positionId: string; summary: string }> {
  const ids: string[] = [];
  const labels: string[] = [];
  for (const seat of seats) {
    env.progress(`Approver ${seat.title}`);
    const name = `${seat.worker.firstName} ${seat.worker.lastName}`;
    await inContext(
      `Creating job ${seat.jobId}`,
      env.d365.ensureJob({ jobId: seat.jobId, description: seat.title }),
    );
    // The worker first, so a new position is never left without its holder by a failed run.
    await inContext(
      `Creating worker ${seat.worker.personnelNumber} (${name})`,
      env.d365.ensureWorker({
        companyId: env.companyId,
        personnelNumber: seat.worker.personnelNumber,
        firstName: seat.worker.firstName,
        lastName: seat.worker.lastName,
        email: approverEmail(seat),
        startDate: today,
      }),
    );
    const p = await inContext(
      `Creating the ${seat.title} position`,
      env.d365.ensurePosition({
        companyId: env.companyId,
        description: seat.title,
        jobId: seat.jobId,
        activation: today,
        anyCompany: true,
      }),
    );
    if (p.created)
      await inContext(
        `Assigning ${seat.worker.personnelNumber} to ${p.positionId}`,
        env.d365.ensureAssignment({
          positionId: p.positionId,
          personnelNumber: seat.worker.personnelNumber,
          primary: true,
          validFrom: today,
        }),
      );
    ids.push(p.positionId);
    labels.push(`${p.positionId} ${seat.title}${p.created ? ` (${name})` : ' (already there)'}`);
  }
  for (let i = 0; i + 1 < ids.length; i++) {
    const [child = '', parent = ''] = [ids[i], ids[i + 1]];
    await inContext(
      `Setting ${child} to report to ${parent}`,
      env.d365.ensureReportingLine({ positionId: child, parentPositionId: parent, validFrom: today }),
    );
  }
  const bottom = ids[0];
  if (!bottom) throw new StepFailure('The environment profile has an empty approval chain');
  return { positionId: bottom, summary: labels.join(' → ') };
}

/**
 * Live: the people records over OData, one at a time, each created only if missing (never changed):
 * every worker and their employment, the user ↔ worker links (the user's Person), and the venue's
 * GL accountant position, which is the hierarchy the journal workflow walks. Who holds it and where
 * it reports come from the environment profile: the first person on the roles sheet under an
 * existing finance director (RSM sandbox), or the prospect's test user under an approval chain the
 * app creates once (Legends TRN). The other positions go in the package only. Dated today when the
 * import's date is later, so a journal submitted straight after provisioning finds its approvers.
 */
async function ensurePeopleRecords(
  env: ActivityEnv,
  data: PeopleData,
  byUpn: ReadonlyMap<string, string>,
): Promise<Record<string, unknown>> {
  const today = new Date(env.now()).toISOString().slice(0, 10);
  const from = (date: string | undefined): string => (date && date < today ? date : today);
  const gl = GL_ACCOUNTANT_SLOT;
  const profile = env.profile;
  let workersCreated = 0;
  let userLinksCreated = 0;

  // Positions need a pay cycle; an environment without one gets it first.
  const payCycle = profile.payCycle.create
    ? await inContext(
        `Creating pay cycle ${profile.payCycle.id}`,
        env.d365.ensurePayCycle({ id: profile.payCycle.id, description: profile.payCycle.description }),
      )
    : null;

  for (const w of data.workers) {
    env.progress(`Worker ${w.personnelNumber} (${w.firstName} ${w.lastName})`);
    const r = await inContext(
      `Creating worker ${w.personnelNumber} and their employment in ${env.companyId}`,
      env.d365.ensureWorker({
        companyId: env.companyId,
        personnelNumber: w.personnelNumber,
        firstName: w.firstName,
        lastName: w.lastName,
        email: w.upn,
        startDate: from(w.employmentStart),
      }),
    );
    if (r.created) workersCreated += 1;
  }
  let accountant: AccountantSeat;
  if (profile.accountantUpn) accountant = await ensureProfileAccountant(env, profile.accountantUpn, today);
  else {
    const w = accountantWorker(data);
    if (!w)
      throw new StepFailure(
        `The first person on the roles sheet has no row on the workers sheet, so nobody can hold the ${gl.title} position`,
      );
    accountant = {
      personnelNumber: w.personnelNumber,
      name: `${w.firstName} ${w.lastName}`,
      start: from(w.employmentStart),
    };
  }
  const chain =
    profile.approvers.kind === 'chain'
      ? await ensureApproverChain(env, profile.approvers.seats, today)
      : null;
  const reportsTo =
    chain?.positionId ?? (profile.approvers.kind === 'existing' ? profile.approvers.positionId : '');
  const start = accountant.start;
  const title = env.template.jobCatalog.find((j) => j.jobId === gl.jobId)?.title ?? gl.title;
  env.progress(`${gl.title} position for ${env.companyId}`);
  const job = await inContext(
    `Creating job ${gl.jobId}`,
    env.d365.ensureJob({ jobId: gl.jobId, description: title }),
  );
  // No ID: D365 numbers it from the Position number sequence, like the hand-made positions.
  const position = await inContext(
    `Creating the ${gl.title} position (job ${gl.jobId}) for ${env.companyId}`,
    env.d365.ensurePosition({
      companyId: env.companyId,
      description: gl.title,
      jobId: gl.jobId,
      activation: start,
    }),
  );
  const { positionId } = position;
  const line = await inContext(
    `Setting ${positionId} to report to ${reportsTo}`,
    env.d365.ensureReportingLine({ positionId, parentPositionId: reportsTo, validFrom: start }),
  );
  const assigned = await inContext(
    `Assigning worker ${accountant.personnelNumber} to ${positionId}`,
    env.d365.ensureAssignment({
      positionId,
      personnelNumber: accountant.personnelNumber,
      primary: true,
      validFrom: start,
    }),
  );
  for (const w of data.workers) {
    const userId = byUpn.get(w.upn.toLowerCase());
    if (!userId) continue;
    const r = await inContext(
      `Linking user ${userId} to worker ${w.personnelNumber}`,
      env.d365.ensurePersonUser({
        userId,
        personnelNumber: w.personnelNumber,
        validFrom: from(w.employmentStart),
      }),
    );
    if (r.created) userLinksCreated += 1;
  }
  return {
    workersCreated,
    userLinksCreated,
    jobCreated: job.created,
    positionCreated: position.created,
    reportingLineCreated: line.created,
    assignmentCreated: assigned.created,
    accountantPosition: `${positionId} → ${accountant.personnelNumber} (${accountant.name}), reports to ${reportsTo}`,
    ...(chain ? { approvalChain: chain.summary } : {}),
    ...(payCycle
      ? { payCycle: `${profile.payCycle.id} ${payCycle.created ? 'created' : 'already there'}` }
      : {}),
    ...(line.note ? { notes: [`${positionId} ${line.note}`] } : {}),
  };
}

/**
 * Prefix a D365 failure with what was being done ("Creating user akowal (…): <D365's reason>"),
 * keeping its type so transient errors are still retried and request errors still stop the step.
 */
async function inContext<T>(what: string, work: Promise<T>): Promise<T> {
  try {
    return await work;
  } catch (err) {
    if (err instanceof TransientD365Error)
      throw new TransientD365Error(`${what}: ${err.message}`, err.status, err.retryAfterMs);
    if (err instanceof D365RequestError) throw new D365RequestError(`${what}: ${err.message}`, err.status);
    if (err instanceof Error) throw new StepFailure(`${what}: ${err.message}`);
    throw err;
  }
}

export interface StepResult {
  status: 'Succeeded' | 'Skipped';
  output: Record<string, unknown>;
  executionIds?: string[];
}

export interface ActivityEnv {
  d365: D365Client;
  run: RunRecord;
  companyId: string;
  template: TemplateConfig;
  flags: { workflowImport: boolean; policyAutomation: boolean };
  pollMs: number;
  timeoutMs: number;
  now: () => number;
  sleep: (ms: number) => Promise<void>;
  /** Persist live progress text for the tracker */
  progress: (text: string, executionIds?: string[]) => void;
  /** Create (or keep) a manual task */
  addTask: (task: Omit<TaskRecord, 'id' | 'requestId' | 'status'>) => void;
  tasks: () => TaskRecord[];
  /** The D365 environment this build targets (environment profile) */
  profile: EnvProfile;
  /** Output of an earlier step in this run (e.g. preflight's template snapshot) */
  stepOutput: (key: StepKey) => Record<string, unknown> | null;
  /** Other requests running a copy from the same template right now */
  otherActiveCopy: () => boolean;
  /** Steps running against the real sandbox; their read-backs are labeled "read from D365". */
  liveSteps: readonly StepKey[];
}

export interface ReportCheck {
  key: string;
  title: string;
  status: 'pass' | 'fail' | 'warning';
  detail: string;
}

export interface ValidationReport {
  checks: ReportCheck[];
  coverage: CoverageMatrix | null;
  generatedAt: string;
}

const answer = (a: Answers, key: string): unknown => a[key];
const str = (a: Answers, key: string): string => {
  const v = answer(a, key);
  return typeof v === 'string' ? v : '';
};

function peopleSnapshot(run: RunRecord): PeopleData {
  return parsePeopleRows(run.peopleSnapshot.map((r, i) => ({ id: String(i), ...r }))).data;
}

async function waitForJobs(
  env: ActivityEnv,
  ids: string[],
  getStatus: (id: string) => Promise<string>,
  terminal: readonly string[],
  label: string,
): Promise<Record<string, string>> {
  const started = env.now();
  const statuses: Record<string, string> = {};
  for (;;) {
    for (const id of ids) statuses[id] = await getStatus(id);
    env.progress(`${label}: ${ids.map((id) => statuses[id]).join(', ')}`, ids);
    if (ids.every((id) => terminal.includes(statuses[id] ?? ''))) return statuses;
    if (env.now() - started > env.timeoutMs)
      throw new StepFailure(`${label} did not finish within ${Math.round(env.timeoutMs / 60000)} minutes`);
    await env.sleep(env.pollMs);
  }
}

type Activity = (env: ActivityEnv) => Promise<StepResult>;

export const ACTIVITIES: Record<StepKey, Activity> = {
  async preflight(env) {
    const a = env.run.answersSnapshot;
    const [copy, people] = await Promise.all([
      env.d365.getCopyProjectInfo(env.template.copyProjectName),
      env.d365.getCopyProjectInfo(env.template.peopleImportProjectName),
    ]);
    if (!copy.exists)
      throw new StepFailure(`Copy project ${env.template.copyProjectName} does not exist in D365`);
    if (!people.exists)
      throw new StepFailure(
        `People import project ${env.template.peopleImportProjectName} does not exist in D365`,
      );
    if (copy.sourceCompany !== env.template.templateLegalEntityId) {
      throw new StepFailure(
        `Copy project copies from ${copy.sourceCompany}, expected ${env.template.templateLegalEntityId}`,
      );
    }
    if (env.otherActiveCopy())
      throw new StepFailure(
        `Another run is copying from template ${env.template.id}; only one copy per template at a time`,
      );
    if (!str(a, 'entity.id')) throw new StepFailure('The answers snapshot has no legal entity ID');
    // Snapshot of the template company before anything runs, so validation can show it is unchanged.
    const templateCounts = await templateRecordCounts(env);
    const total = Object.values(templateCounts).reduce((n, c) => n + c, 0);
    return {
      status: 'Succeeded',
      output: {
        copyProject: copy.entityCount + ' entities',
        templateCompany: copy.sourceCompany,
        templateSnapshot: `${Object.keys(templateCounts).length} entity sets, ${total} records in ${env.template.templateLegalEntityId}`,
        templateCounts,
      },
    };
  },

  async createLegalEntity(env) {
    const a = env.run.answersSnapshot;
    const name = str(a, 'entity.legalName');
    const existing = await env.d365.getLegalEntity(env.companyId);
    if (existing) {
      if (existing.name === name)
        return {
          status: 'Skipped',
          output: {
            reason: `${env.companyId} already exists with the approved name`,
            calendar: await ensureWorkflowCalendar(env),
          },
        };
      throw new StepFailure(`${env.companyId} already exists in D365 as "${existing.name}"`);
    }
    const address = (answer(a, 'entity.address') as Address | undefined) ?? null;
    await env.d365.createLegalEntity({
      id: env.companyId,
      name,
      searchName: str(a, 'entity.searchName'),
      timeZone: str(a, 'entity.timeZone'),
      address,
    });
    return {
      status: 'Succeeded',
      output: {
        created: env.companyId,
        name,
        ...(address ? { address: `${address.city}, ${address.state}` } : {}),
        calendar: await ensureWorkflowCalendar(env),
      },
    };
  },

  async addressAndRegistration(env) {
    const a = env.run.answersSnapshot;
    const address = answer(a, 'entity.address') as Address | undefined;
    // Written with the company when this run created it; set here only on a company that existed.
    const setOnCreate = Boolean(env.stepOutput('createLegalEntity')?.['address']);
    try {
      if (address && !setOnCreate) await env.d365.setLegalEntityAddress(env.companyId, address);
      const ein = str(a, 'entity.ein');
      // The document store only holds the masked EIN; the Azure API decrypts the full value here (spec 14).
      if (ein) await env.d365.setTaxRegistration(env.companyId, ein);
      return {
        status: 'Succeeded',
        output: {
          address: address ? `${address.city}, ${address.state}` : 'none',
          taxRegistration: ein ? 'set (masked)' : 'none',
        },
      };
    } catch (err) {
      env.addTask({
        taskKey: 'address-registration',
        description: `Set the primary address and tax registration on ${env.companyId} (${err instanceof Error ? err.message : 'failed'})`,
        ownerUpn: null,
        dueDate: null,
      });
      return { status: 'Skipped', output: { manualTask: 'address-registration' } };
    }
  },

  async copyFromTemplate(env) {
    const a = env.run.answersSnapshot;
    const copyNumberSequences = answer(a, 'finance.copyNumberSequences') !== false;
    const { executionIds } = await env.d365.startCopy({
      projectName: env.template.copyProjectName,
      destinationCompany: env.companyId,
      copyNumberSequences,
      resetToSmallest: copyNumberSequences && answer(a, 'finance.resetToSmallest') !== false,
    });
    const statuses = await waitForJobs(
      env,
      executionIds,
      (id) => env.d365.getExecutionStatus(id),
      DMF_TERMINAL_STATUSES,
      'Copy into legal entity',
    );
    const failed = executionIds.filter((id) => statuses[id] !== 'Succeeded');
    if (failed.length) {
      const errors = (await Promise.all(failed.map((id) => env.d365.getExecutionErrors(id)))).flat();
      throw new StepFailure(
        `Copy ${failed.map((id) => `${id} ${statuses[id]}`).join(', ')}: ${errors.join(' ')}`,
      );
    }
    return {
      status: 'Succeeded',
      output: { jobs: executionIds.length, from: env.template.templateLegalEntityId },
      executionIds,
    };
  },

  async venueOverrides(env) {
    const a = env.run.answersSnapshot;
    const applied: string[] = [];
    const dimension = str(a, 'finance.venueDimensionValue');
    if (dimension) {
      await env.d365.applyOverride(env.companyId, { key: 'venueDimensionValue', value: dimension });
      applied.push(`Venue dimension ${dimension}`);
    }
    // Only answers that differ from the template are written; the copy already brought the rest.
    const deviating = (a['__deviations'] as string[] | undefined) ?? [];
    for (const key of [
      'procurement.matchingPolicy',
      'finance.currency',
      'finance.fiscalCalendar',
      'finance.chartOfAccounts',
    ] as const) {
      const v = answer(a, key);
      if (typeof v === 'string' && v && deviating.includes(key)) {
        await env.d365.applyOverride(env.companyId, { key, value: v });
        applied.push(`${key} = ${v}`);
      }
    }
    // Ledger overlays: recorded for this company only; the ledger package carries them into D365
    // (journal names + Journal Control V2, main account legal entity overrides).
    const overlays = ledgerOverlaysOf(a);
    for (const j of overlays.journals) {
      await env.d365.applyOverride(env.companyId, {
        key: `ledger.journal.${j.name}`,
        value: `${j.fromAccount}-${j.toAccount}`,
      });
      applied.push(describeJournalOverlay(j));
    }
    for (const o of overlays.accounts) {
      await env.d365.applyOverride(env.companyId, {
        key: `ledger.account.${o.mainAccountId}`,
        value: [o.suspend ? 'suspended' : '', o.defaultDimensionValue].filter(Boolean).join(' '),
      });
      applied.push(describeAccountOverlay(o, templateLedgerFor(env.profile.templateCompany)));
    }
    if (overlays.journals.length || overlays.accounts.length)
      env.addTask({
        taskKey: 'ledger-overlays',
        description:
          `Import the ledger package in ${env.companyId} (Data package tab): it carries the venue's ledger overlays, ` +
          `then confirm them in D365 (journal names, journal control, main account overrides for ${env.companyId} only)`,
        ownerUpn: null,
        dueDate: null,
      });
    return { status: applied.length ? 'Succeeded' : 'Skipped', output: { applied } };
  },

  async importPeople(env) {
    const data = peopleSnapshot(env.run);
    // Live slice: every user, worker and employment and the user ↔ worker links (the user's Person),
    // and the first person on the roles sheet assigned to the existing Accountant position, are
    // written to D365 over OData, skipping what exists. The full package still goes through the
    // (mock) recurring data job, which mirrors every row for the read-backs.
    let usersCreated: number | undefined;
    let created: Record<string, unknown> = {};
    if (env.liveSteps.includes('importPeople')) {
      env.progress(`Creating users in D365 (${data.users.length} on the users sheet)`);
      const users = await ensureUsers(env, data);
      usersCreated = users.created;
      created = await ensurePeopleRecords(env, data, users.byUpn);
    }
    const [positions, workers] = await Promise.all([
      env.d365.getPositions(env.companyId),
      env.d365.getWorkers(env.companyId),
    ]);
    const allThere =
      data.positions.every((p) => positions.some((x) => x.positionId === p.positionId)) &&
      data.workers.every((w) => workers.some((x) => x.personnelNumber === w.personnelNumber));
    if (allThere && data.positions.length > 0)
      return {
        status: 'Skipped',
        output: {
          reason: 'Every approved row already exists in D365',
          ...(usersCreated === undefined ? {} : { usersCreated, ...created }),
        },
      };

    const pkg = buildPeoplePackage(data, env.companyId, {
      asOf: new Date(env.now()).toISOString().slice(0, 10),
      env: peoplePackageEnv(env.profile),
    });
    const zip = await zipPackage(pkg);
    env.progress('Enqueueing package to the recurring data job');
    const { messageId } = await env.d365.enqueueRecurringPackage({
      activityId: RECURRING_JOBS.people.activityId,
      company: env.companyId,
      zip,
    });
    const statuses = await waitForJobs(
      env,
      [messageId],
      (id) => env.d365.getRecurringMessageStatus(id),
      RECURRING_TERMINAL,
      'Recurring data job LG-VENUE-PEOPLE',
    );
    if (statuses[messageId] !== 'Processed') {
      const errors = await env.d365.getExecutionErrors(messageId);
      throw new StepFailure(`People package ${statuses[messageId]}: ${errors.join(' ')}`);
    }
    const records = pkg.entities.reduce((n, e) => n + e.rows.length, 0);
    return {
      status: 'Succeeded',
      output: {
        ...(usersCreated === undefined ? {} : { usersCreated, ...created }),
        package: pkg.definitionGroup,
        entities: pkg.entities.length,
        records,
        messageId,
      },
      executionIds: [messageId],
    };
  },

  async assignSecurity(env) {
    const data = peopleSnapshot(env.run);
    const assigned = await inContext(
      `Reading role assignments in ${env.companyId}`,
      env.d365.listUserRoleAssignments(env.companyId),
    );
    // Users usually exist already (Import people and approvals creates them); any missing are created here.
    const { byUpn, created: usersCreated } = await ensureUsers(env, data);
    // The profile's accountant is an existing D365 user, not on the users sheet.
    const accountantUpn = env.profile.accountantUpn;
    const userIds = new Map(byUpn);
    if (accountantUpn) {
      const user = (
        await inContext(`Looking up user ${accountantUpn}`, env.d365.searchUsers(accountantUpn))
      ).find((u) => u.upn.toLowerCase() === accountantUpn.toLowerCase());
      if (!user) throw new StepFailure(`${accountantUpn} is not a D365 user in this environment`);
      userIds.set(accountantUpn.toLowerCase(), user.userId);
    }
    let verified = 0;
    let added = 0;
    for (const r of rolesToAssign(data, accountantUpn)) {
      const userId = userIds.get(r.upn.toLowerCase());
      if (!userId) throw new StepFailure(`${r.upn} has a role but is not in the users sheet`);
      if (assigned.some((x) => x.userId === userId && x.roleId === r.roleId)) {
        verified += 1;
        continue;
      }
      // Scoped to the venue company only; never falls back to broader access.
      await inContext(
        `Assigning ${r.roleId} to ${userId} in ${env.companyId}`,
        env.d365.assignUserRole(userId, r.roleId, env.companyId),
      );
      added += 1;
    }
    return { status: 'Succeeded', output: { usersCreated, verified, added, scope: env.companyId } };
  },

  async importCustomersVendors(env) {
    const data = peopleSnapshot(env.run);
    if (data.customers.length === 0 && data.vendors.length === 0)
      return { status: 'Skipped', output: { reason: 'The import has no customers or vendors' } };
    const [customers, vendors] = await Promise.all([
      inContext(`Reading customers in ${env.companyId}`, env.d365.listCustomers(env.companyId)),
      inContext(`Reading vendors in ${env.companyId}`, env.d365.listVendors(env.companyId)),
    ]);
    const haveCustomer = new Set(customers.map((c) => c.customerAccount));
    const haveVendor = new Set(vendors.map((v) => v.vendorId));
    let customersCreated = 0;
    let vendorsCreated = 0;
    // Rows already in the company (copied from the template, or created by an earlier run) are skipped.
    for (const c of data.customers.filter((x) => !haveCustomer.has(x.customerAccount))) {
      env.progress(
        `Creating customer ${c.customerAccount} (${customersCreated + 1} of ${data.customers.length})`,
      );
      await inContext(
        `Creating customer ${c.customerAccount}`,
        env.d365.createCustomer({
          companyId: env.companyId,
          customerAccount: c.customerAccount,
          name: c.name,
          group: c.customerGroup,
          currency: c.currency,
          phone: c.phone,
          email: c.email,
          street: c.street,
          city: c.city,
          state: c.state,
          zip: c.zip,
          paymentTerms: c.paymentTerms,
        }),
      );
      customersCreated += 1;
    }
    for (const v of data.vendors.filter((x) => !haveVendor.has(x.vendorAccount))) {
      env.progress(`Creating vendor ${v.vendorAccount} (${vendorsCreated + 1} of ${data.vendors.length})`);
      await inContext(
        `Creating vendor ${v.vendorAccount}`,
        env.d365.createVendor({
          companyId: env.companyId,
          vendorAccount: v.vendorAccount,
          name: v.name,
          group: v.vendorGroup,
          currency: v.currency,
          phone: v.phone,
          email: v.email,
          street: v.street,
          city: v.city,
          state: v.state,
          zip: v.zip,
          paymentTerms: v.paymentTerms,
          paymentMethod: v.paymentMethod,
        }),
      );
      vendorsCreated += 1;
    }
    return {
      status: customersCreated + vendorsCreated > 0 ? 'Succeeded' : 'Skipped',
      output: {
        customersCreated,
        customersAlreadyThere: data.customers.length - customersCreated,
        vendorsCreated,
        vendorsAlreadyThere: data.vendors.length - vendorsCreated,
      },
    };
  },

  async importProjects(env) {
    const data = peopleSnapshot(env.run);
    if (data.projectContracts.length === 0 && data.projects.length === 0)
      return { status: 'Skipped', output: { reason: 'The import has no project contracts or projects' } };
    const [contracts, projects] = await Promise.all([
      inContext(
        `Reading project contracts in ${env.companyId}`,
        env.d365.listProjectContracts(env.companyId),
      ),
      inContext(`Reading projects in ${env.companyId}`, env.d365.listProjects(env.companyId)),
    ]);
    const haveContract = new Set(contracts.map((c) => c.contractId));
    const haveProject = new Set(projects.map((p) => p.projectId));
    let contractsCreated = 0;
    let projectsCreated = 0;
    for (const c of data.projectContracts.filter((x) => !haveContract.has(x.contractId))) {
      env.progress(`Creating project contract ${c.contractId}`);
      await inContext(
        `Creating project contract ${c.contractId}`,
        env.d365.createProjectContract({
          companyId: env.companyId,
          contractId: c.contractId,
          name: c.name,
          customerAccount: c.customerAccount,
        }),
      );
      contractsCreated += 1;
    }
    // Parents before children, so every parent project exists when its sub-projects are created.
    for (const p of projectsInCreationOrder(data.projects).filter((x) => !haveProject.has(x.projectId))) {
      env.progress(`Creating project ${p.projectId} (${projectsCreated + 1} of ${data.projects.length})`);
      await inContext(
        `Creating project ${p.projectId}`,
        env.d365.createProject({
          companyId: env.companyId,
          projectId: p.projectId,
          name: p.name,
          projectType: p.projectType,
          projectGroup: p.projectGroup,
          contractId: p.contractId,
          parentProjectId: p.parentProjectId,
          managerPersonnelNumber: p.managerPersonnelNumber,
          controllerPersonnelNumber: p.controllerPersonnelNumber,
          startDate: p.startDate,
          endDate: p.endDate,
        }),
      );
      projectsCreated += 1;
    }
    return {
      status: contractsCreated + projectsCreated > 0 ? 'Succeeded' : 'Skipped',
      output: {
        contractsCreated,
        projectsCreated,
        alreadyThere:
          data.projectContracts.length + data.projects.length - contractsCreated - projectsCreated,
      },
    };
  },

  async policiesAndParameters(env) {
    if (env.flags.policyAutomation) {
      await env.d365.addCompanyToPolicy('SigningLimit', env.companyId);
      await env.d365.addCompanyToPolicy('Purchasing', env.companyId);
      await env.d365.setRequireExplicitlyAssignedUsers(env.companyId, true);
      return {
        status: 'Succeeded',
        output: { policies: ['SigningLimit', 'Purchasing'], requireExplicitlyAssignedUsers: true },
      };
    }
    env.addTask({
      taskKey: 'signing-limit-policy',
      description: `Add ${env.companyId} to the signing limit and purchasing policies`,
      ownerUpn: null,
      dueDate: null,
    });
    env.addTask({
      taskKey: 'require-explicit-users',
      description: `Turn on "Require explicitly assigned users" (Workflow parameters) in ${env.companyId}`,
      ownerUpn: null,
      dueDate: null,
    });
    return {
      status: 'Skipped',
      output: {
        reason: 'Policy automation is off (FEATURE_POLICY_AUTOMATION); manual tasks created',
        manualTasks: 2,
      },
    };
  },

  async importWorkflows(env) {
    const selected = (answer(env.run.answersSnapshot, 'workflows.selected') as string[] | undefined) ?? [];
    const library = env.template.workflowLibrary.filter(
      (w) => selected.includes(w.key) && w.documentType !== 'PurchaseRequisition',
    );
    if (!env.flags.workflowImport) {
      for (const w of library) {
        env.addTask({
          taskKey: `workflow-${w.key}`,
          description: `Import workflow "${w.name}" unchanged into ${env.companyId} from ${w.xmlBlobUrl}`,
          ownerUpn: null,
          dueDate: null,
        });
      }
      return {
        status: 'Skipped',
        output: {
          reason: 'Workflow import is off (FEATURE_WORKFLOW_IMPORT); manual tasks created',
          workflows: library.map((w) => w.key),
        },
      };
    }
    const results: string[] = [];
    for (const w of library) {
      if (await env.d365.isWorkflowActive(env.companyId, w.key)) continue;
      const r = await env.d365.importWorkflow({
        company: env.companyId,
        workflowKey: w.key,
        xmlBlobUrl: w.xmlBlobUrl,
        activate: true,
      });
      results.push(`${w.key} → ${r.workflowId}`);
    }
    return { status: 'Succeeded', output: { imported: results } };
  },

  async validate(env) {
    return { status: 'Succeeded', output: { report: await buildValidationReport(env) } };
  },

  async notify(env) {
    // GRAPH_NOTIFY is off: log only (spec 11.1 step 11).
    return {
      status: 'Succeeded',
      output: { recipients: [env.run.startedBy, 'requester', 'approvers'], channel: 'log only' },
    };
  },
};

// ── validation report (spec 11.4) ────────────────────────────────────────────

/** Record counts of the copied entity sets in the template company. */
async function templateRecordCounts(env: ActivityEnv): Promise<Record<string, number>> {
  const counts = await Promise.all(
    COPIED_ENTITY_SETS.map(
      async (set) => [set, await env.d365.countRecords(set, env.template.templateLegalEntityId)] as const,
    ),
  );
  return Object.fromEntries(counts);
}

const minutes = (ms: number): string => {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
};

/**
 * The work calendar the journal workflow's due dates use (24hr), created with the company so the
 * workflow imports with its calendar. Not fatal: the ledger package creates it too, so a failure
 * is reported in the step output instead of stopping the run.
 */
async function ensureWorkflowCalendar(env: ActivityEnv): Promise<string> {
  const spec = workCalendarSpec(env.companyId);
  try {
    const { created } = await env.d365.ensureWorkCalendar(spec);
    return `${spec.calendarId} ${created ? 'created' : 'already exists'}`;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return `${spec.calendarId} not created (${message}); the ledger package creates it`;
  }
}

/**
 * The import's roles, plus the GL accountant's roles (Accountant) for the venue's first user: the
 * person who enters and submits general journals. Added here as well, so a request whose people
 * import predates the GL accountant position (applied before 2026-10-23) still gets it.
 */
export function rolesToAssign(
  data: PeopleData,
  /** The environment profile's accountant, who gets the GL accountant roles instead */
  accountantUpn: string | null = null,
): Array<{ upn: string; roleId: string }> {
  const holder = accountantUpn ?? data.users[0]?.upn;
  const has = (upn: string, roleId: string): boolean =>
    data.roles.some((x) => x.upn.toLowerCase() === upn.toLowerCase() && x.roleId === roleId);
  const extra = holder
    ? GL_ACCOUNTANT_SLOT.roles
        .filter((roleId) => !has(holder, roleId))
        .map((roleId) => ({ upn: holder, roleId }))
    : [];
  return [...data.roles, ...extra];
}

async function readBackPeople(env: ActivityEnv): Promise<{ data: PeopleData; ctx: PeopleContext }> {
  const d365 = env.d365;
  const [positions, workers, limits, roles, templateLimits] = await Promise.all([
    d365.getPositions(env.companyId),
    d365.getWorkers(env.companyId),
    d365.getApprovalLimits(env.companyId),
    d365.listUserRoleAssignments(env.companyId),
    d365.getApprovalLimits(env.template.templateLegalEntityId),
  ]);
  const users = await Promise.all([...new Set(roles.map((r) => r.userId))].map((u) => d365.getUser(u)));
  const upnOf = (userId: string): string => users.find((u) => u?.userId === userId)?.upn ?? userId;

  const existingPositions = new Map<string, ExistingPosition>();
  // Every line that leaves the venue (GM → regional director, GL accountant → finance director),
  // followed up to the top.
  const outside = [
    ...new Set(
      positions.flatMap((p) =>
        p.reportsToPositionId && !positions.some((x) => x.positionId === p.reportsToPositionId)
          ? [p.reportsToPositionId]
          : [],
      ),
    ),
  ];
  for (const start of outside) {
    let parent: string | null = start;
    for (let depth = 0; parent && !existingPositions.has(parent) && depth < 10; depth++) {
      const p = await d365.getPosition(parent);
      if (!p) break;
      existingPositions.set(p.positionId, {
        positionId: p.positionId,
        title: p.title,
        jobId: p.jobId,
        companyId: p.companyId,
        reportsToPositionId: p.reportsToPositionId,
        holder:
          p.holderPersonnelNumber && p.holderName
            ? { name: p.holderName, upn: p.holderUpn, personnelNumber: p.holderPersonnelNumber }
            : null,
      });
      parent = p.reportsToPositionId;
    }
  }
  // D365 positions don't say which assignment is primary; the approved import does.
  const approvedAssignments = peopleSnapshot(env.run).assignments;
  const isPrimary = (positionId: string, personnelNumber: string): boolean =>
    approvedAssignments.find((a) => a.positionId === positionId && a.personnelNumber === personnelNumber)
      ?.primary ?? true;

  const data: PeopleData = {
    users: users.flatMap((u, i) =>
      u ? [{ upn: u.upn, displayName: u.name, email: u.upn, rowNumber: i + 2 }] : [],
    ),
    workers: workers.map((w, i) => ({
      personnelNumber: w.personnelNumber,
      firstName: w.firstName,
      lastName: w.lastName,
      upn: w.upn ?? '',
      employmentStart: w.employmentStart,
      rowNumber: i + 2,
    })),
    positions: positions.map((p, i) => ({
      positionId: p.positionId,
      title: p.title,
      jobId: p.jobId,
      departmentId: p.departmentId ?? undefined,
      reportsToPositionId: p.reportsToPositionId ?? '',
      rowNumber: i + 2,
    })),
    assignments: positions.flatMap((p, i) =>
      p.holderPersonnelNumber
        ? [
            {
              positionId: p.positionId,
              personnelNumber: p.holderPersonnelNumber,
              startDate: '',
              primary: isPrimary(p.positionId, p.holderPersonnelNumber),
              rowNumber: i + 2,
            },
          ]
        : [],
    ),
    approvalLimits: limits.map((l, i) => ({
      jobId: l.jobId,
      documentType: l.documentType,
      amountUsd: l.amountUsd,
      rowNumber: i + 2,
    })),
    roles: roles.map((r, i) => ({ upn: upnOf(r.userId), roleId: r.roleId, rowNumber: i + 2 })),
    // Master data is checked separately (checkMasterData); routing needs none of it.
    customers: [],
    vendors: [],
    projectContracts: [],
    projects: [],
  };
  const ctx: PeopleContext = {
    mode: 'existingVenue',
    companyId: env.companyId,
    template: env.template,
    answers: null,
    directory: new Map(),
    existingPositions,
    positionIdsElsewhere: new Set(),
    personnelNumbersElsewhere: new Set(),
    existingUserUpns: new Set(),
    existingPersonnelNumbers: new Set(),
    baseLimits: templateLimits.filter((l) => !limits.some((x) => x.jobId === l.jobId)),
    existingRoleHolders: [],
  };
  return { data, ctx };
}

export async function buildValidationReport(env: ActivityEnv): Promise<ValidationReport> {
  const a = env.run.answersSnapshot;
  const d365 = env.d365;
  const checks: ReportCheck[] = [];
  const add = (key: string, title: string, ok: boolean | 'warning', detail: string) =>
    checks.push({ key, title, status: ok === 'warning' ? 'warning' : ok ? 'pass' : 'fail', detail });

  const le = await d365.getLegalEntity(env.companyId);
  const address = answer(a, 'entity.address') as Address | undefined;
  add(
    'legal-entity',
    'Legal entity',
    !!le && le.name === str(a, 'entity.legalName') && (!address || le.address?.zip === address.zip),
    (le
      ? `${le.id} "${le.name}"${le.address ? `, ${le.address.city} ${le.address.state}` : ', no address'}`
      : 'Not found') + (env.liveSteps.includes('createLegalEntity') ? ' · read from D365' : ''),
  );

  const counts = await Promise.all(
    COPIED_ENTITY_SETS.map(async (set) => ({
      set,
      venue: await d365.countRecords(set, env.companyId),
      template: await d365.countRecords(set, env.template.templateLegalEntityId),
    })),
  );
  const short = counts.filter((c) => c.venue !== c.template);
  add(
    'copied-setup',
    'Copied setup',
    short.length === 0,
    short.length
      ? short.map((c) => `${c.set}: ${c.venue} of ${c.template}`).join('; ')
      : `${counts.length} entities match the template record counts`,
  );

  const seq = counts.find((c) => c.set === 'NumberSequenceReferences');
  add(
    'number-sequences',
    'Number sequences',
    !seq || seq.venue === seq.template,
    seq ? `${seq.venue} of ${seq.template} references` : 'not copied',
  );

  const approved = peopleSnapshot(env.run);
  const { data, ctx } = await readBackPeople(env);
  const missing = [
    ...approved.positions
      .filter((p) => !data.positions.some((x) => x.positionId === p.positionId))
      .map((p) => `position ${p.positionId}`),
    ...approved.workers
      .filter((w) => !data.workers.some((x) => x.personnelNumber === w.personnelNumber))
      .map((w) => `worker ${w.personnelNumber}`),
    ...approved.positions
      .filter(
        (p) =>
          data.positions.find((x) => x.positionId === p.positionId)?.reportsToPositionId !==
          p.reportsToPositionId,
      )
      .map((p) => `reports-to of ${p.positionId}`),
    ...approved.assignments
      .filter(
        (s) =>
          !data.assignments.some(
            (x) => x.positionId === s.positionId && x.personnelNumber === s.personnelNumber,
          ),
      )
      .map((s) => `assignment ${s.positionId}`),
  ];
  add(
    'people',
    'People',
    missing.length === 0,
    missing.length
      ? `Missing: ${[...new Set(missing)].join(', ')}`
      : `${data.positions.length} positions, ${data.workers.length} workers and their reporting lines are in ${env.companyId}`,
  );

  const people = buildRoutingPeople(data, ctx);
  const coverage = computeCoverage(
    people,
    env.template.routing,
    [...people.primaryPositionByUpn.keys()],
    coverageAmounts(
      data.approvalLimits.filter((l) => approved.approvalLimits.some((x) => x.jobId === l.jobId)),
    ),
  );
  add(
    'approval-routing',
    'Approval routing',
    coverage.gapCount === 0,
    coverage.gapCount
      ? `${coverage.gapCount} amount bands without an approver (read back from D365)`
      : 'Every document type and amount band reaches an approver, on data read back from D365',
  );

  const selected = (answer(a, 'workflows.selected') as string[] | undefined) ?? [];
  const perCompany = env.template.workflowLibrary.filter(
    (w) => selected.includes(w.key) && w.documentType !== 'PurchaseRequisition',
  );
  const wfStatus = await Promise.all(
    perCompany.map(async (w) => ({
      w,
      active: await d365.isWorkflowActive(env.companyId, w.key),
      task: env.tasks().find((t) => t.taskKey === `workflow-${w.key}`),
    })),
  );
  const wfBad = wfStatus.filter((x) => !x.active && !x.task);
  add(
    'workflows',
    'Workflows',
    wfBad.length === 0,
    wfStatus
      .map(
        (x) =>
          `${x.w.key}: ${x.active ? 'active' : x.task ? `manual task ${x.task.status.toLowerCase()}` : 'missing'}`,
      )
      .join(', ') + (selected.includes('PR') ? '; PR is organization-wide' : ''),
  );

  const actual = new Set(data.roles.map((r) => `${r.upn}|${r.roleId}`));
  const expected = new Set(approved.roles.map((r) => `${r.upn}|${r.roleId}`));
  const extra = [...actual].filter((x) => !expected.has(x));
  const absent = [...expected].filter((x) => !actual.has(x));
  add(
    'security',
    'Security',
    extra.length === 0 && absent.length === 0,
    extra.length || absent.length
      ? `Missing ${absent.length}, unexpected ${extra.length}`
      : `${expected.size} role assignments, all scoped to ${env.companyId}`,
  );

  // Master data checks appear only when the import had those tabs filled in.
  const readFrom = (step: StepKey): string => (env.liveSteps.includes(step) ? ' · created in D365' : '');
  if (approved.customers.length || approved.vendors.length) {
    const [customers, vendors] = await Promise.all([
      d365.listCustomers(env.companyId),
      d365.listVendors(env.companyId),
    ]);
    const absentParties = [
      ...approved.customers
        .filter((c) => !customers.some((x) => x.customerAccount === c.customerAccount))
        .map((c) => `customer ${c.customerAccount}`),
      ...approved.vendors
        .filter((v) => !vendors.some((x) => x.vendorId === v.vendorAccount))
        .map((v) => `vendor ${v.vendorAccount}`),
    ];
    add(
      'customers-vendors',
      'Customers and vendors',
      absentParties.length === 0,
      (absentParties.length
        ? `Missing: ${absentParties.join(', ')}`
        : `${approved.customers.length} customers and ${approved.vendors.length} vendors are in ${env.companyId}`) +
        readFrom('importCustomersVendors'),
    );
  }
  if (approved.projectContracts.length || approved.projects.length) {
    const [contracts, projects] = await Promise.all([
      d365.listProjectContracts(env.companyId),
      d365.listProjects(env.companyId),
    ]);
    const absentProjects = [
      ...approved.projectContracts
        .filter((c) => !contracts.some((x) => x.contractId === c.contractId))
        .map((c) => `contract ${c.contractId}`),
      ...approved.projects
        .filter((p) => !projects.some((x) => x.projectId === p.projectId))
        .map((p) => `project ${p.projectId}`),
    ];
    add(
      'projects',
      'Projects',
      absentProjects.length === 0,
      (absentProjects.length
        ? `Missing: ${absentProjects.join(', ')}`
        : `${approved.projects.length} projects and ${approved.projectContracts.length} contracts are in ${env.companyId}`) +
        readFrom('importProjects'),
    );
  }

  // Ledger overlays (B: journal names with journal control, C: main account overrides).
  const overlays = ledgerOverlaysOf(a);
  const overlayLines = [
    ...overlays.journals.map(describeJournalOverlay),
    ...overlays.accounts.map((o) =>
      describeAccountOverlay(o, templateLedgerFor(env.profile.templateCompany)),
    ),
  ];
  if (overlayLines.length) {
    const applied = env.stepOutput('venueOverrides')?.['applied'];
    const recorded = Array.isArray(applied) ? applied.map(String) : [];
    const missing = overlayLines.filter((l) => !recorded.includes(l));
    const task = env.tasks().find((t) => t.taskKey === 'ledger-overlays');
    add(
      'ledger-overlays',
      'Ledger overlays',
      missing.length ? false : task?.status === 'Done' ? true : 'warning',
      (missing.length ? `Not applied: ${missing.join('; ')}` : overlayLines.join('; ')) +
        ` · ${env.companyId} only, in the ledger package` +
        (task ? ` · D365 import task ${task.status.toLowerCase()}` : ''),
    );
  }

  // Evidence the template company was not changed: its record counts now against the snapshot
  // preflight took before anything ran.
  const before = env.stepOutput('preflight')?.['templateCounts'] as Record<string, number> | undefined;
  const after = await templateRecordCounts(env);
  const changedSets = before ? Object.entries(after).filter(([set, n]) => before[set] !== n) : [];
  const templateTotal = Object.values(after).reduce((n, c) => n + c, 0);
  add(
    'template-unchanged',
    'Template unchanged',
    !before ? 'warning' : changedSets.length === 0,
    !before
      ? `No snapshot of ${env.template.templateLegalEntityId} from preflight to compare`
      : changedSets.length
        ? `Changed in ${env.template.templateLegalEntityId}: ${changedSets.map(([s, n]) => `${s} ${before[s] ?? 0} → ${n}`).join('; ')}`
        : `${env.template.templateLegalEntityId}: ${Object.keys(after).length} entity sets, ${templateTotal} records, the same as before provisioning. ` +
          `Overlays and copied setup are written for ${env.companyId} only (shared chart of accounts untouched)`,
  );

  // How long it took and what is left by hand.
  const tasks = env.tasks();
  const elapsed = env.now() - new Date(env.run.startedAt).getTime();
  const open = tasks.filter((t) => t.status !== 'Done');
  add(
    'elapsed',
    'Time and manual steps',
    true,
    `Provisioning ran ${minutes(elapsed)} · ${tasks.length} manual step${tasks.length === 1 ? '' : 's'}` +
      (tasks.length ? ` (${open.length} open): ${tasks.map((t) => t.taskKey).join(', ')}` : ''),
  );

  const incomplete = tasks.filter((t) => !t.ownerUpn || !t.dueDate);
  add(
    'manual-tasks',
    'Manual tasks',
    incomplete.length === 0,
    `${tasks.length} tasks; ${incomplete.length ? `${incomplete.length} without owner or due date` : 'all have an owner and due date'}`,
  );

  return { checks, coverage, generatedAt: new Date(env.now()).toISOString() };
}
